import { dueDayMs, formatBusinessDate } from '@/utils/date-utils';

/**
 * Where an association stands against its management `terminationDate` (#417).
 *
 * - `none`        — no termination date set.
 * - `terminating` — set, and today is on or before it: still managed, but going away.
 * - `terminated`  — today is after it.
 *
 * Both dates are business days (midnight-UTC markers of the ET day). The termination date is
 * the LAST managed day, so the association is `terminating` through that whole day.
 */
export type TerminationState = 'none' | 'terminating' | 'terminated';

export function terminationState(terminationDate: unknown, todayMs: number): TerminationState {
	const day = dueDayMs(terminationDate);
	if (day === null) return 'none';
	return todayMs > day ? 'terminated' : 'terminating';
}

/**
 * True when a new work order — created today, due `dueDate` — would fall AFTER the association's
 * termination date. This is the hard-block case: non-admins are refused, work-order admins must
 * acknowledge it. A due date on the termination day itself is still inside management.
 */
export function workOrderPastTermination(
	terminationDate: unknown,
	todayMs: number,
	dueDate?: unknown,
): boolean {
	const day = dueDayMs(terminationDate);
	if (day === null) return false;
	return todayMs > day || dueDatePastTermination(terminationDate, dueDate);
}

/**
 * True when `dueDate` falls after the termination date. The rule for RESCHEDULING an existing
 * work order: moving its due date past the date is refused, but editing a work order that already
 * exists (completing it, adding notes) after termination is not.
 */
export function dueDatePastTermination(terminationDate: unknown, dueDate: unknown): boolean {
	const day = dueDayMs(terminationDate);
	const due = dueDayMs(dueDate);
	return day !== null && due !== null && due > day;
}

/** Badge text for the hub header and tiles — "Terminating 11/30/2026" / "Terminated 11/30/2026". */
export function terminationLabel(terminationDate: unknown, todayMs: number): string | null {
	const state = terminationState(terminationDate, todayMs);
	if (state === 'none') return null;
	const day = formatBusinessDate(dueDayMs(terminationDate));
	return state === 'terminated' ? `Terminated ${day}` : `Terminating ${day}`;
}

/** Error code the API returns when a work order lands past the termination date. */
export const ASSOCIATION_TERMINATED_CODE = 'association_terminated';
